import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import QRCode from "qrcode";
import { CheckCircle2, Copy, Download, ExternalLink, Feather, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SocialShareRow, copyText } from "@/components/SocialShareRow";
import { useI18n } from "@/context/I18nContext";

const POINTS = ["groups.intro_pt_1", "groups.intro_pt_2", "groups.intro_pt_3", "groups.intro_pt_5", "groups.intro_pt_10", "groups.intro_pt_9"];
const W = 1080;
const H = 1350;

function CardArt({ t, lang, link, qr, innerRef }) {
  const ar = lang === "ar";
  const body = ar ? "Tajawal, system-ui, sans-serif" : "Manrope, system-ui, sans-serif";
  const head = ar ? "Tajawal, system-ui, sans-serif" : "Outfit, system-ui, sans-serif";
  return (
    <div
      ref={innerRef}
      dir={lang === "ar" ? "rtl" : "ltr"}
      style={{ width: W, height: H, background: "linear-gradient(160deg,#EFF3ED 0%,#ffffff 45%,#F7F9F6 100%)", color: "#2C3325", padding: 72, boxSizing: "border-box", display: "flex", flexDirection: "column", fontFamily: body }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, color: "#6B8E4F", fontSize: 22, letterSpacing: 6, fontWeight: 700, textTransform: "uppercase" }}>
        <Feather style={{ width: 34, height: 34 }} />
        {t("app.brand")}
      </div>
      <div style={{ fontFamily: head, fontSize: 64, fontWeight: 700, lineHeight: 1.1, marginTop: 28 }}>
        {t("groups.intro_title")}
      </div>
      <div style={{ fontSize: 26, lineHeight: 1.45, marginTop: 22, opacity: 0.8 }}>{t("groups.intro_body")}</div>
      <div style={{ marginTop: 34, display: "flex", flexDirection: "column", gap: 18, flex: 1 }}>
        {POINTS.map((k) => (
          <div key={k} style={{ display: "flex", gap: 16, alignItems: "flex-start", fontSize: 26, lineHeight: 1.4 }}>
            <CheckCircle2 style={{ width: 32, height: 32, color: "#6B8E4F", flexShrink: 0, marginTop: 2 }} />
            <span>{t(k)}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 28, background: "#6B8E4F", color: "#fff", borderRadius: 28, padding: "26px 32px" }}>
        {qr && <img src={qr} alt="QR" style={{ width: 168, height: 168, borderRadius: 16, background: "#fff", padding: 8, boxSizing: "border-box" }} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: head, fontSize: 34, fontWeight: 700 }}>{t("share.cta")}</div>
          <div style={{ fontSize: 24, opacity: 0.9, marginTop: 8 }}>{t("share.scan")}</div>
          <div dir="ltr" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 26, marginTop: 12, wordBreak: "break-all", textAlign: lang === "ar" ? "right" : "left" }}>{link.replace(/^https?:\/\//, "")}</div>
        </div>
      </div>
    </div>
  );
}

export const ShareWelcomeCard = () => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0.3);
  const artRef = useRef(null);
  const wrapRef = useRef(null);
  const link = window.location.origin;

  useEffect(() => {
    if (!open) return;
    QRCode.toDataURL(link, { width: 336, margin: 1, color: { dark: "#2C3325", light: "#ffffff" } }).then(setQr);
    const fit = () => wrapRef.current && setScale(Math.min(1, (wrapRef.current.clientWidth - 2) / W));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [open, link]);

  const render = async () => {
    const dataUrl = await toPng(artRef.current, { width: W, height: H, pixelRatio: 1, cacheBust: true, skipFonts: false }).catch(() =>
      toPng(artRef.current, { width: W, height: H, pixelRatio: 1, skipFonts: true })
    );
    const blob = await (await fetch(dataUrl)).blob();
    return { dataUrl, blob };
  };

  const share = async () => {
    setBusy(true);
    try {
      const { blob } = await render();
      const file = new File([blob], "breeders-tracking-app.png", { type: "image/png" });
      const text = `${t("share.msg")} ${link}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: t("groups.intro_title") });
      } else {
        await download(blob);
        await copyText(text);
        toast.success(t("share.downloaded"));
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t("share.failed"));
    }
    setBusy(false);
  };

  const download = async (blob) => {
    const b = blob || (await render()).blob;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(b);
    a.download = "breeders-tracking-app.png";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const copyLink = async () => {
    const ok = await copyText(link);
    ok ? toast.success(t("share.copied")) : toast.error(t("share.failed"));
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="share-card-button" className="gap-1.5">
        <Share2 className="w-4 h-4" />
        {t("share.button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="share-card-dialog">
          <DialogHeader>
            <DialogTitle>{t("share.title")}</DialogTitle>
          </DialogHeader>
          <div ref={wrapRef} dir="ltr" className="relative w-full overflow-hidden rounded-xl border border-app-border bg-cream" style={{ height: H * scale }}>
            <div style={{ position: "absolute", left: 0, top: 0, transform: `scale(${scale})`, transformOrigin: "top left", width: W, height: H }}>
              <CardArt t={t} lang={lang} link={link} qr={qr} innerRef={artRef} />
            </div>
          </div>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="inline-flex items-center gap-1.5 text-sm font-mono text-olive underline underline-offset-2 hover:text-olive-hover break-all"
            data-testid="share-card-link"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            {link}
          </a>
          <SocialShareRow
            title={t("groups.intro_title")}
            text={`${t("share.msg")} ${link}`}
            link={link}
            getImage={async () => (await render()).blob}
          />
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="share-copy-link" className="gap-1.5">
              <Copy className="w-4 h-4" />{t("share.copy_link")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => download()} disabled={busy} data-testid="share-download" className="gap-1.5">
              <Download className="w-4 h-4" />{t("share.download")}
            </Button>
            <Button size="sm" onClick={share} disabled={busy || !qr} data-testid="share-now" className="gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              {t("share.share")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
